/**
 * Export column definitions for employee Excel export.
 * Column order matches create-employee forms: PersonalDetailsForm, BankDetailsForm,
 * FamilyDetailsForm, QualificationDetailsForm, ProfessionalDetailsForm, StatutoryDetailsForm, OrgDetailsForm.
 */

/** DD/MM/YYYY for all exported dates */
export const formatDateDdMmYyyy = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return "";
  }
};

const getNameById = (id, list) => {
  if (id === undefined || id === null || id === "") return "";
  if (!list || !Array.isArray(list)) return "";
  const n = Number(id);
  const item = list.find(
    (obj) =>
      obj.id === id || obj.id === n || String(obj.id) === String(id)
  );
  if (!item) return "";
  const name = item.name || (item.user && item.user.name);
  return name ? String(name).trim() : "";
};

const resolveMasterName = (id, list, emp, nestedKey) => {
  if (nestedKey && emp && emp[nestedKey]) {
    const rel = emp[nestedKey];
    const n = rel.user?.name || rel.name;
    if (n) return String(n).trim();
  }
  return getNameById(id, list);
};

const yesNo = (v) => {
  if (v === true || v === "Yes" || v === "yes") return "Yes";
  if (v === false || v === "No" || v === "no") return "No";
  return "";
};

const triStateYesNo = (v) => {
  if (v === true || v === "Yes" || v === "yes") return "Yes";
  if (v === false || v === "No" || v === "no") return "No";
  return "";
};

/**
 * Resolve reason id to display name using the appropriate Reasons list.
 * @param {"resignation"|"termination"} kind
 */
const resolveReasonFromList = (reasonVal, list, emp, kind = "resignation") => {
  const textFallback =
    kind === "termination"
      ? emp?.terminationReason
      : emp?.resignationReason;
  if (reasonVal === undefined || reasonVal === null || reasonVal === "") {
    if (textFallback && typeof textFallback === "string") {
      const s = textFallback.trim();
      if (s && !/^\d+$/.test(s)) return s;
    }
    return "";
  }
  const str = String(reasonVal).trim();
  if (str && Number.isNaN(Number(str))) return str;
  const n = Number(str);
  const row = (list || []).find(
    (r) => r.id === n || r.id === reasonVal || String(r.id) === String(reasonVal)
  );
  if (row && row.name) return String(row.name).trim();
  if (textFallback && typeof textFallback === "string") {
    const s = textFallback.trim();
    if (s) return s;
  }
  return str;
};

/** Age string like the Personal tab card */
function formatAgeFromDob(dateOfBirth) {
  if (!dateOfBirth) return "";
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    if (isNaN(birthDate.getTime())) return "";
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    if (months < 0 || (months === 0 && today.getDate() < birthDate.getDate())) {
      years--;
      months += 12;
    }
    if (today.getDate() < birthDate.getDate()) {
      months--;
      if (months < 0) {
        months += 12;
        years--;
      }
    }
    let s = `${years} ${years === 1 ? "year" : "years"}`;
    if (months > 0) s += ` ${months} ${months === 1 ? "month" : "months"}`;
    return s;
  } catch {
    return "";
  }
}

function getAddressObj(emp) {
  const a = emp.address;
  if (!a || typeof a !== "object") return {};
  return a;
}

function sameAsPresentAddress(emp) {
  const a = getAddressObj(emp);
  const cur = (v) => String(v ?? "").trim();
  return (
    cur(a.address) === cur(a.permanentAddress) &&
    cur(a.city) === cur(a.permanentCity) &&
    cur(a.state) === cur(a.permanentState) &&
    cur(a.pincode) === cur(a.permanentPincode)
  );
}

function hasPhoto(emp) {
  const p = emp.photo;
  return !!(p && String(p).trim());
}

/** Degree value → label (QualificationDetailsForm staticOptions) */
const DEGREE_LABELS = {
  "10th": "10th / Matriculation",
  "12th": "12th / Higher Secondary",
  diploma: "Diploma",
  graduation: "Graduation / Bachelor's Degree",
  postGraduation: "Post Graduation / Master's Degree",
  doctorate: "Doctorate / Ph.D.",
  professional: "Professional Certification",
  others: "Others",
};

function degreeDisplay(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  return DEGREE_LABELS[s] || s;
}

function genderDisplay(g) {
  const x = String(g || "").toLowerCase();
  if (x === "male") return "Male";
  if (x === "female") return "Female";
  if (x === "other") return "Other";
  return g ? String(g) : "";
}

/** Match Excel example: blank / NA → "Not Available" for phone & Aadhaar */
function cellNotAvailable(val) {
  const s = String(val ?? "").trim();
  if (!s) return "Not Available";
  if (/^na$/i.test(s) || /^n\/a$/i.test(s)) return "Not Available";
  if (/^not\s*available$/i.test(s)) return "Not Available";
  return s;
}

const EMPTY_LEADING = "";

/**
 * Apply vertical merges for the first `mergeLeadingCols` columns (Sl No … shared fields).
 * `mergeGroups`: data-row indices into `rows` (0 = first data row), not counting header.
 * Header row count is 1 when using json_to_sheet (row 0 = headers).
 */
export function applyGroupedExportMerges(
  worksheet,
  mergeGroups,
  mergeLeadingCols,
  headerRowCount = 1
) {
  if (!worksheet || !mergeGroups || !mergeGroups.length || !mergeLeadingCols) return;
  const merges = [];
  for (const g of mergeGroups) {
    const n = g.rowCount || 0;
    if (n <= 1) continue;
    const r0 = headerRowCount + g.startRow;
    const r1 = headerRowCount + g.startRow + n - 1;
    for (let c = 0; c < mergeLeadingCols; c++) {
      merges.push({ s: { r: r0, c }, e: { r: r1, c } });
    }
  }
  if (merges.length) worksheet["!merges"] = merges;
}

function normalizeFamilyMember(m) {
  if (!m || typeof m !== "object") return null;
  const rawPhone = m.phone || m.mobile_number || m.mobileNumber || "";
  const rawAadhaar = m.aadhaarNumber || m.aadhaar_number || "";
  return {
    name: m.name || "",
    dateOfBirth: m.dateOfBirth || m.date_of_birth || "",
    relationship: m.relationship || "",
    gender: genderDisplay(m.gender),
    phone: cellNotAvailable(rawPhone),
    aadhaarNumber: cellNotAvailable(rawAadhaar),
    dependent: !!(m.dependent || m.dependent_status),
    nominee: !!(m.nominee || m.nominee_status),
    sharePercentage:
      m.sharePercentage != null && m.sharePercentage !== ""
        ? m.sharePercentage
        : m.share_percentage != null && m.share_percentage !== ""
          ? m.share_percentage
          : "",
    hasPhoto: !!(m.photo && String(m.photo).trim()),
  };
}

function normalizeQualification(q) {
  if (!q || typeof q !== "object") return null;
  const degree = q.degree ?? q.degree_id;
  const y = q.yearOfPassing ?? q.year_of_passing;
  return {
    degree: degreeDisplay(degree),
    yearOfCompletion: y != null && y !== "" ? String(y) : "",
    institution: q.institution || "",
    percentage: q.percentage != null && q.percentage !== "" ? String(q.percentage) : "",
    specialization: q.specialization || "",
  };
}

function normalizeExperience(x) {
  if (!x || typeof x !== "object") return null;
  return {
    company: x.company || x.company_name || "",
    position: x.position || x.designation || "",
    fromDate: x.fromDate || x.from_date || "",
    toDate: x.toDate || x.to_date || "",
    description: x.description || "",
  };
}

/**
 * PersonalDetailsForm field order (labels aligned with form).
 */
export function getPersonalDetailsExportRows(employees, dateFormatter = formatDateDdMmYyyy) {
  return employees.map((e, idx) => {
    const a = getAddressObj(e);
    const dob = e.user?.dateOfBirth || e.dateOfBirth;
    return {
      "Sl No": idx + 1,
      "Employee Code": e.empCode || "",
      "Employee Name": e.user?.name || e.name || "",
      "Photo Uploaded": hasPhoto(e) ? "Yes" : "No",
      "Father's Name": e.fatherName || "",
      "Marital Status": e.user?.maritalStatus || e.maritalStatus || "",
      Age: formatAgeFromDob(dob),
      "House Number/Street/Flat (Present)": a.address || "",
      "City (Present)": a.city || "",
      "State (Present)": a.state || "",
      "Country (Present)": a.country || "",
      "Pincode (Present)": a.pincode || "",
      "Same as Present Address": sameAsPresentAddress(e) ? "Yes" : "No",
      "House Number/Street/Flat (Permanent)": a.permanentAddress || "",
      "City (Permanent)": a.permanentCity || "",
      "State (Permanent)": a.permanentState || "",
      "Country (Permanent)": a.permanentCountry || "",
      "Pincode (Permanent)": a.permanentPincode || "",
      "Emergency Contact Name": e.emergencyContactName || "",
      "Emergency Contact Number": e.emergencyContactPhone || "",
      "Emergency Contact Relationship": e.emergencyContactRelation || "",
    };
  });
}

/**
 * BankDetailsForm — only the four fields on the form.
 */
export function getBankDetailsExportRows(employees) {
  return employees.map((e, idx) => ({
    "Sl No": idx + 1,
    "Employee Code": e.empCode || "",
    Name: e.user?.name || e.name || "",
    "Bank Name": e.bankName || "",
    "Bank Account No": e.accountNumber || "",
    "IFSC Code": e.ifscCode || "",
    Branch: e.bankBranch || "",
  }));
}

/**
 * StatutoryDetailsForm — PF block, EPS, ESI block in screen order.
 * When only ESI applies (no PF), PF/EPS columns are blank like the hidden form.
 */
export function getStatutoryDetailsExportRows(employees) {
  return employees.map((e, idx) => {
    const pfOn = e.pfApplicable !== false;
    const esiOn = !!e.esiApplicable;
    const onlyEsi = esiOn && !pfOn;

    const row = {
      "Sl No": idx + 1,
      "Employee Code": e.empCode || "",
      Name: e.user?.name || e.name || "",
    };

    if (!onlyEsi) {
      row["Do you have UAN number?"] = triStateYesNo(e.hasUAN);
      row["PF UAN Number"] = e.pfUANNumber || "";
      row["PF Member ID"] = e.pfMemberId || "";
      row["Are you eligible for EPS deduction in your Previous Company?"] =
        triStateYesNo(e.epsEligiblePreviousCompany);
    } else {
      row["Do you have UAN number?"] = "";
      row["PF UAN Number"] = "";
      row["PF Member ID"] = "";
      row["Are you eligible for EPS deduction in your Previous Company?"] = "";
    }

    if (esiOn) {
      row["Do you hold an ESI IP number?"] = triStateYesNo(e.hasESIIP);
      row["ESI IP Number"] = e.hasESIIP ? e.esiIPNumber || "" : "";
      row["ESI State"] = e.esiState || "";
      row["ESI District"] = e.esiDistrict || "";
      row["ESI Dispensary"] = e.esiDispensary || "";
    } else {
      row["Do you hold an ESI IP number?"] = "";
      row["ESI IP Number"] = "";
      row["ESI State"] = "";
      row["ESI District"] = "";
      row["ESI Dispensary"] = "";
    }

    return row;
  });
}

/**
 * FamilyDetailsForm — grouped rows: one row per family member; orphan employees get one row.
 * Returns `{ rows, mergeLeadingCols, mergeGroups }` for Excel vertical merges on shared columns.
 */
export function getFamilyDetailsExportRows(employees, dateFormatter = formatDateDdMmYyyy) {
  const rows = [];
  const mergeGroups = [];
  /** Sl No, Employee Code, Employee Name, Having no family */
  const mergeLeadingCols = 4;

  const baseLeading = (slNo, code, empName, havingNoFamily) => ({
    "Sl No": slNo,
    "Employee Code": code,
    "Employee Name": empName,
    "Having no family": havingNoFamily,
  });

  const emptyLeading = () => ({
    "Sl No": EMPTY_LEADING,
    "Employee Code": EMPTY_LEADING,
    "Employee Name": EMPTY_LEADING,
    "Having no family": EMPTY_LEADING,
  });

  employees.forEach((e, empIdx) => {
    const slNo = empIdx + 1;
    const code = e.empCode || "";
    const name = e.user?.name || e.name || "";
    const orphan = !!e.isOrphan;
    const members = Array.isArray(e.familyMembers) ? e.familyMembers : [];

    if (orphan || members.length === 0) {
      rows.push({
        ...baseLeading(slNo, code, name, orphan ? "Yes" : "No"),
        "Member Name": "",
        "Date of Birth": "",
        Relationship: "",
        Gender: "",
        "Mobile Number": "",
        "Aadhaar No": "",
        Dependent: "",
        Nominee: "",
        "Share %": "",
        "Photo Upload": "",
      });
      return;
    }

    const startRow = rows.length;
    let rowIdx = 0;
    members.forEach((raw) => {
      const m = normalizeFamilyMember(raw);
      if (!m) return;
      const detail = {
        "Member Name": m.name,
        "Date of Birth": dateFormatter(m.dateOfBirth),
        Relationship: m.relationship,
        Gender: m.gender,
        "Mobile Number": m.phone,
        "Aadhaar No": m.aadhaarNumber,
        Dependent: yesNo(m.dependent),
        Nominee: yesNo(m.nominee),
        "Share %":
          m.nominee && m.sharePercentage !== "" && m.sharePercentage != null
            ? String(m.sharePercentage)
            : "",
        "Photo Upload": m.hasPhoto ? "Yes" : "No",
      };
      if (rowIdx === 0) {
        rows.push({
          ...baseLeading(slNo, code, name, "No"),
          ...detail,
        });
      } else {
        rows.push({
          ...emptyLeading(),
          ...detail,
        });
      }
      rowIdx++;
    });

    const rowCount = rows.length - startRow;
    if (rowCount > 1) {
      mergeGroups.push({ startRow, rowCount });
    }
  });

  return { rows, mergeLeadingCols, mergeGroups };
}

/**
 * QualificationDetailsForm — one row per qualification; lessThanPrimary-only employees get one row.
 * Returns `{ rows, mergeLeadingCols, mergeGroups }` for merged Sl No / Code / Name / Less than primary.
 */
export function getQualificationDetailsExportRows(employees) {
  const rows = [];
  const mergeGroups = [];
  const mergeLeadingCols = 4;

  const baseLeading = (slNo, code, empName, ltpLabel) => ({
    "Sl No": slNo,
    "Employee Code": code,
    "Employee Name": empName,
    "Less than primary education": ltpLabel,
  });

  const emptyLeading = () => ({
    "Sl No": EMPTY_LEADING,
    "Employee Code": EMPTY_LEADING,
    "Employee Name": EMPTY_LEADING,
    "Less than primary education": EMPTY_LEADING,
  });

  employees.forEach((e, empIdx) => {
    const slNo = empIdx + 1;
    const code = e.empCode || "";
    const name = e.user?.name || e.name || "";
    const ltp = !!e.lessThanPrimary;
    const quals = Array.isArray(e.qualifications) ? e.qualifications : [];

    if (ltp) {
      rows.push({
        ...baseLeading(slNo, code, name, "Yes"),
        Degree: "",
        "Year of Completion": "",
        "Name of the Institution": "",
        "Percentage/Grade/Class": "",
        Specialization: "",
      });
      return;
    }

    if (quals.length === 0) {
      rows.push({
        ...baseLeading(slNo, code, name, "No"),
        Degree: "",
        "Year of Completion": "",
        "Name of the Institution": "",
        "Percentage/Grade/Class": "",
        Specialization: "",
      });
      return;
    }

    const startRow = rows.length;
    let rowIdx = 0;
    quals.forEach((raw) => {
      const q = normalizeQualification(raw);
      if (!q) return;
      const detail = {
        Degree: q.degree,
        "Year of Completion": q.yearOfCompletion,
        "Name of the Institution": q.institution,
        "Percentage/Grade/Class": q.percentage,
        Specialization: q.specialization,
      };
      if (rowIdx === 0) {
        rows.push({
          ...baseLeading(slNo, code, name, "No"),
          ...detail,
        });
      } else {
        rows.push({
          ...emptyLeading(),
          ...detail,
        });
      }
      rowIdx++;
    });

    const rowCount = rows.length - startRow;
    if (rowCount > 1) {
      mergeGroups.push({ startRow, rowCount });
    }
  });

  return { rows, mergeLeadingCols, mergeGroups };
}

/**
 * ProfessionalDetailsForm — one row per prior job; freshers get a single row.
 * Returns `{ rows, mergeLeadingCols, mergeGroups }` for merged Sl No / Code / Name / Fresher.
 */
export function getProfessionalDetailsExportRows(employees, dateFormatter = formatDateDdMmYyyy) {
  const rows = [];
  const mergeGroups = [];
  const mergeLeadingCols = 4;

  const baseLeading = (slNo, code, empName, fresherLabel) => ({
    "Sl No": slNo,
    "Employee Code": code,
    "Employee Name": empName,
    Fresher: fresherLabel,
  });

  const emptyLeading = () => ({
    "Sl No": EMPTY_LEADING,
    "Employee Code": EMPTY_LEADING,
    "Employee Name": EMPTY_LEADING,
    Fresher: EMPTY_LEADING,
  });

  employees.forEach((e, empIdx) => {
    const slNo = empIdx + 1;
    const code = e.empCode || "";
    const name = e.user?.name || e.name || "";
    const fresher = !!e.isFresher;
    const exps = Array.isArray(e.experiences) ? e.experiences : [];

    if (fresher || exps.length === 0) {
      rows.push({
        ...baseLeading(slNo, code, name, fresher ? "Yes" : "No"),
        "Previous Company Name": "",
        Position: "",
        "From Date": "",
        "To Date": "",
        Description: "",
      });
      return;
    }

    const startRow = rows.length;
    let rowIdx = 0;
    exps.forEach((raw) => {
      const x = normalizeExperience(raw);
      if (!x) return;
      const detail = {
        "Previous Company Name": x.company,
        Position: x.position,
        "From Date": dateFormatter(x.fromDate),
        "To Date": dateFormatter(x.toDate),
        Description: x.description,
      };
      if (rowIdx === 0) {
        rows.push({
          ...baseLeading(slNo, code, name, "No"),
          ...detail,
        });
      } else {
        rows.push({
          ...emptyLeading(),
          ...detail,
        });
      }
      rowIdx++;
    });

    const rowCount = rows.length - startRow;
    if (rowCount > 1) {
      mergeGroups.push({ startRow, rowCount });
    }
  });

  return { rows, mergeLeadingCols, mergeGroups };
}

/**
 * Organization details columns — OrgDetailsForm.jsx
 */
function createOrgDetailsColumns(masters = {}, dateFormatter = formatDateDdMmYyyy) {
  const {
    branches = [],
    designations = [],
    departments = [],
    subDepartments = [],
    grades = [],
    categories = [],
    employees: employeesList = [],
    resignationReasons = [],
    terminationReasons = [],
  } = masters;

  return [
    ["Employee Code", (e) => e.empCode || ""],
    ["Name", (e) => e.user?.name || e.name || ""],
    ["Gender", (e) => e.user?.gender || e.gender || ""],
    ["Date of Birth", (e) => dateFormatter(e.user?.dateOfBirth || e.dateOfBirth)],
    ["Branch", (e) => resolveMasterName(e.branchId, branches, e, "branch")],
    ["Designation", (e) => resolveMasterName(e.designationId, designations, e, "designation")],
    ["Department", (e) => resolveMasterName(e.departmentId, departments, e, "department")],
    ["Sub Department", (e) => resolveMasterName(e.subDepartmentId, subDepartments, e, "subDepartment")],
    ["Grade", (e) => resolveMasterName(e.gradeId, grades, e, "grade")],
    ["Category", (e) => resolveMasterName(e.categoryId, categories, e, "category")],
    ["Reporting Manager", (e) =>
      resolveMasterName(e.reportingManagerId, employeesList, e, "reportingManager")],
    ["Date of Join", (e) => dateFormatter(e.joiningDate || e.dateOfJoin || e.joinDate)],
    ["Employee Type", (e) => e.employmentType || ""],
    ["Employment Status", (e) => e.employmentStatus || ""],
    ["Confirmation Date", (e) => dateFormatter(e.confirmationDate)],
    ["Date of Resignation", (e) =>
      dateFormatter(e.resignationDate || e.dateOfResignation)],
    ["Date of Relieved", (e) =>
      dateFormatter(
        e.relievedDate || e.dateOfRelieving || e.dateOfLeaving || e.exitDate
      )],
    [
      "Resignation Reason",
      (e) => {
        const st = e.employmentStatus || "";
        if (st !== "Resigned" && st !== "Relieved") return "";
        return resolveReasonFromList(e.reason, resignationReasons, e, "resignation");
      },
    ],
    [
      "Termination Date",
      (e) =>
        e.employmentStatus === "Terminated"
          ? dateFormatter(e.terminationDate)
          : "",
    ],
    [
      "Termination Reason",
      (e) => {
        if (e.employmentStatus !== "Terminated") return "";
        return resolveReasonFromList(e.reason, terminationReasons, e, "termination");
      },
    ],
    ["Mobile Number", (e) => e.user?.phone || e.phone || ""],
    ["Personal Email", (e) => e.user?.email || ""],
    ["Official Email", (e) => e.email || ""],
    ["Blood Group", (e) => e.user?.bloodGroup || e.bloodGroup || ""],
    ["Aadhaar No", (e) => e.aadharNumber || e.user?.aadhaarNumber || ""],
    ["PAN", (e) => e.panNumber || e.user?.panNumber || ""],
    ["CTC (Cost to Company)", (e) => (e.ctc != null && e.ctc !== "" ? e.ctc : "")],
    ["Remarks", (e) => (e.remarks != null ? String(e.remarks) : "")],
    ["PF Applicable", (e) => yesNo(e.pfApplicable !== false)],
    ["Ceiling PF", (e) => yesNo(e.ceilingPf !== false)],
    ["EPS", (e) => yesNo(!!e.epsApplicable)],
    ["ESI Applicable", (e) => yesNo(!!e.esiApplicable)],
    ["PT Applicable", (e) => yesNo(e.ptApplicable !== false)],
    ["Gratuity Applicable", (e) => yesNo(e.gratuityApplicable !== false)],
  ];
}

export function getOrgDetailsExportRows(
  employees,
  masters = {},
  dateFormatter = formatDateDdMmYyyy
) {
  const columns = createOrgDetailsColumns(masters, dateFormatter);
  return employees.map((emp) => {
    const row = {};
    columns.forEach(([header, fn]) => {
      row[header] = fn(emp);
    });
    return row;
  });
}

/** Full employee export: org columns + personal + bank + statutory (legacy sheet). */
export function getAllEmployeesExportRows(
  employees,
  masters = {},
  dateFormatter = formatDateDdMmYyyy
) {
  const orgCols = createOrgDetailsColumns(masters, dateFormatter);
  const personalCols = [
    ["Marital Status", (e) => e.user?.maritalStatus || e.maritalStatus || ""],
    ["Father Name", (e) => e.fatherName || ""],
    ["Current Address", (e) => e.address?.address || ""],
    ["Current City", (e) => e.address?.city || ""],
    ["Current State", (e) => e.address?.state || ""],
    ["Current Pincode", (e) => e.address?.pincode || ""],
    ["Emergency Contact Name", (e) => e.emergencyContactName || ""],
    ["Emergency Contact Phone", (e) => e.emergencyContactPhone || ""],
    ["Emergency Contact Relation", (e) => e.emergencyContactRelation || ""],
    ["Permanent Address", (e) => e.address?.permanentAddress || ""],
    ["Permanent City", (e) => e.address?.permanentCity || ""],
    ["Permanent State", (e) => e.address?.permanentState || ""],
    ["Permanent Pincode", (e) => e.address?.permanentPincode || ""],
    ["Bank Name", (e) => e.bankName || ""],
    ["Account Number", (e) => e.accountNumber || ""],
    ["IFSC Code", (e) => e.ifscCode || ""],
    ["Bank Branch", (e) => e.bankBranch || ""],
    [
      "Has UAN",
      (e) =>
        e.hasUAN === true || e.hasUAN === "Yes"
          ? "Yes"
          : e.hasUAN === false || e.hasUAN === "No"
            ? "No"
            : "",
    ],
    ["PF UAN Number", (e) => e.pfUANNumber || ""],
    [
      "EPS Eligible Previous Company",
      (e) =>
        e.epsEligiblePreviousCompany === true || e.epsEligiblePreviousCompany === "Yes"
          ? "Yes"
          : e.epsEligiblePreviousCompany === false ||
              e.epsEligiblePreviousCompany === "No"
            ? "No"
            : "",
    ],
    [
      "Has ESI IP",
      (e) =>
        e.hasESIIP === true || e.hasESIIP === "Yes"
          ? "Yes"
          : e.hasESIIP === false || e.hasESIIP === "No"
            ? "No"
            : "",
    ],
    ["ESI IP Number", (e) => e.esiIPNumber || ""],
    ["ESI State", (e) => e.esiState || ""],
    ["ESI District", (e) => e.esiDistrict || ""],
    ["ESI Dispensary", (e) => e.esiDispensary || ""],
  ];
  const columns = [...orgCols, ...personalCols];
  return employees.map((emp) => {
    const row = {};
    columns.forEach(([header, fn]) => {
      row[header] = fn(emp);
    });
    return row;
  });
}
